import React, { useState, useEffect } from 'react';
import { mockProducts, mockQuotes, mockAuth } from '../services/mockDatabase';
import { Send, Sparkles, Check, Info } from 'lucide-react';

const AVAILABLE_COLORS = [
  { name: 'Red', hex: '#ef4444' },
  { name: 'Pink', hex: '#ec4899' },
  { name: 'Purple', hex: '#a855f7' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Green', hex: '#22c55e' },
  { name: 'Yellow', hex: '#eab308' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Black', hex: '#1f2937' },
  { name: 'White', hex: '#ffffff' }
];

const Customize = ({ productId, onAddToCart }) => {
  const [product, setProduct] = useState(null);
  const [user, setUser] = useState(null);
  
  // Customization selections
  const [selectedColors, setSelectedColors] = useState([]);
  const [customText, setCustomText] = useState('');
  const [notes, setNotes] = useState('');
  
  // Custom Quote request selections (for non-existing items)
  const [quoteDescription, setQuoteDescription] = useState('');
  
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    setUser(mockAuth.getCurrentUser());
    if (productId) {
      const found = mockProducts.getProducts().find(p => p.id === productId);
      if (found) {
        setProduct(found);
      }
    }
  }, [productId]);

  const handleColorToggle = (colorName) => {
    if (selectedColors.includes(colorName)) {
      setSelectedColors(selectedColors.filter(c => c !== colorName));
    } else {
      setSelectedColors([...selectedColors, colorName]);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Add custom options to the cart item details
    const customizedItem = {
      ...product,
      id: `${product.id}_custom_${Date.now()}`, // Unique ID for cart item
      baseProductId: product.id,
      name: `${product.name} (Custom)`,
      customOptions: {
        colorScheme: selectedColors,
        letters: customText,
        notes: notes
      },
      // Slightly higher price for custom additions if text is requested
      price: product.price + (customText ? 1.00 : 0.00),
      quantity: 1
    };

    onAddToCart(customizedItem);
    setMessage({ text: 'Custom item added to cart!', type: 'success' });
    setTimeout(() => {
      window.location.hash = '#/cart';
    }, 1500);
  };

  const handleRequestQuote = (e) => {
    if (e) e.preventDefault();
    
    if (!user) {
      setMessage({ text: 'Please sign in to request a quote.', type: 'error' });
      return;
    }

    try {
      mockQuotes.createQuoteRequest(
        product ? `Customization for ${product.name}: ${notes}` : quoteDescription,
        customText,
        selectedColors,
        product ? product.id : null
      );
      setMessage({ text: 'Quote request submitted successfully! Georgia will review it shortly.', type: 'success' });
      setQuoteDescription('');
      setSelectedColors([]);
      setCustomText('');
      setNotes('');
      setTimeout(() => {
        window.location.hash = '#/profile';
      }, 3000);
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Do nothing on Enter keypress to prevent accidental quote submission while typing
  };

  // Build the array of beads for the visual preview string
  const buildBeads = () => {
    const beads = [];
    const colorCount = selectedColors.length;

    // 1. Leading color beads
    if (colorCount > 0) {
      for (let i = 0; i < 4; i++) {
        const colorName = selectedColors[i % colorCount];
        const colorObj = AVAILABLE_COLORS.find(c => c.name === colorName);
        beads.push({ type: 'color', color: colorObj?.hex || '#ccc', label: colorName });
      }
    }

    // 2. Letter beads in the middle
    if (customText) {
      customText.toUpperCase().split('').forEach(letter => {
        beads.push({ type: 'letter', letter });
      });
    }

    // 3. Trailing color beads
    if (colorCount > 0) {
      for (let i = 0; i < 4; i++) {
        const colorName = selectedColors[(i + 2) % colorCount]; // Offset slightly for pattern variation
        const colorObj = AVAILABLE_COLORS.find(c => c.name === colorName);
        beads.push({ type: 'color', color: colorObj?.hex || '#ccc', label: colorName });
      }
    }

    return beads;
  };

  const currentBeadString = buildBeads();

  if (productId && !product) {
    return <div className="text-center py-20 text-gray-500 italic">Loading product details...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto my-12 px-6">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center gap-2">
          <Sparkles className="text-yellow-400" />
          {product ? `Customize ${product.name}` : 'Request a Custom Design'}
        </h2>
        <p className="text-gray-600 mt-2">
          {product 
            ? 'Add personalized letters or choose your favorite bead colors!' 
            : 'Looking for a specific animal or bracelet pattern? Ask Georgia for a custom quote.'}
        </p>
      </div>

      {message.text && (
        <div className={`p-4 rounded-xl text-sm font-semibold mb-8 text-center border ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border-green-200' 
            : 'bg-red-50 text-red-600 border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {!user && (
        <div className="flex items-center gap-3 bg-yellow-50 text-yellow-800 p-4 rounded-2xl text-sm border border-yellow-200 mb-8">
          <Info size={20} className="shrink-0" />
          <span>
            You are viewing this as a guest. Please{' '}
            <a href="#/login" className="font-bold underline text-yellow-900">Sign In</a>{' '}
            to submit custom quotes and track order history.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Left Side: Customize Form Controls */}
        <div className="bg-white p-6 md:p-8 rounded-3xl shadow-lg border border-pink-50">
          <form onSubmit={handleFormSubmit} className="space-y-6">
            
            {/* If requesting a general quote (no product id) */}
            {!product && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">What would you like designed?</label>
                <textarea
                  required
                  rows={4}
                  value={quoteDescription}
                  onChange={(e) => setQuoteDescription(e.target.value)}
                  placeholder="Example: I'd love a green and orange bead alligator keychain, or a custom friendship bracelet with pastel rainbow patterns."
                  className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                />
              </div>
            )}

            {/* Custom Bead Color Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">Choose Bead Colors</label>
              <div className="grid grid-cols-3 gap-2">
                {AVAILABLE_COLORS.map(color => {
                  const isSelected = selectedColors.includes(color.name);
                  return (
                    <button
                      type="button"
                      key={color.name}
                      onClick={() => handleColorToggle(color.name)}
                      className={`flex items-center gap-2 p-2 rounded-xl border text-xs font-semibold transition-all ${
                        isSelected 
                          ? 'border-pink-500 bg-pink-50/50 text-pink-700' 
                          : 'border-gray-100 hover:border-gray-200 text-gray-600 bg-gray-50/30'
                      }`}
                    >
                      <span 
                        className="w-4 h-4 rounded-full border border-gray-300 inline-block shadow-sm"
                        style={{ backgroundColor: color.hex }}
                      />
                      <span>{color.name}</span>
                      {isSelected && <Check size={12} className="ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Letters/Word Option (Bracelets) */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Add Name/Letters <span className="text-gray-400 font-normal">(+$1.00)</span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="e.g. SPARKLE"
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all uppercase tracking-widest text-center font-bold text-lg"
              />
              <p className="text-xs text-gray-400 mt-1">Maximum 15 letter beads.</p>
            </div>

            {/* Custom Design Notes */}
            {product && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Design Notes / Instructions</label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Specify any special patterns, charms, or adjustments you want Georgia to know."
                  className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all text-sm"
                />
              </div>
            )}

            {/* Submit Action */}
            <div className="pt-4 flex flex-col gap-3">
              {product ? (
                <>
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-4 rounded-xl shadow-md hover:shadow-lg transition-transform hover:-translate-y-0.5 active:scale-95 text-center text-sm"
                  >
                    Add Custom Design to Cart
                  </button>
                  <button
                    type="button"
                    onClick={handleRequestQuote}
                    disabled={!user}
                    className="w-full bg-purple-50 text-purple-700 hover:bg-purple-100 font-bold py-3 rounded-xl transition text-sm disabled:opacity-50"
                  >
                    Submit as Quote Request instead
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleRequestQuote}
                  disabled={!user}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl shadow-md hover:shadow-lg transition-transform hover:-translate-y-0.5 active:scale-95 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                >
                  <Send size={16} /> Send Quote Request to Georgia
                </button>
              )}
            </div>

          </form>
        </div>

        {/* Right Side: Virtual Preview / Info Panel */}
        <div className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-6 md:p-8 rounded-3xl border border-pink-100 flex flex-col items-center justify-center text-center">
          <h3 className="font-bold text-gray-800 text-lg mb-4">Design Studio Preview</h3>
          
          {/* Virtual bead simulator container */}
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-md w-full max-w-md flex flex-col items-center mb-6 border border-pink-100">
            <div className="w-12 h-12 rounded-full bg-pink-100/50 flex items-center justify-center text-pink-500 mb-4">
              <Sparkles size={20} />
            </div>
            
            {/* Bead String Simulator */}
            <div className="relative w-full py-8 flex items-center justify-center min-h-[90px] bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
              {/* String/Thread Line */}
              <div className="absolute left-6 right-6 h-1 bg-gray-300 rounded-full top-1/2 transform -translate-y-1/2 z-0"></div>
              
              {/* Beads Container */}
              <div className="relative z-10 flex gap-1 items-center justify-center flex-wrap max-w-[280px]">
                {currentBeadString.map((bead, idx) => {
                  if (bead.type === 'color') {
                    return (
                      <div 
                        key={idx}
                        className="w-6 h-6 rounded-full border-2 border-gray-200 shadow-md transform hover:scale-115 transition-transform duration-200 cursor-pointer shrink-0"
                        style={{ backgroundColor: bead.color }}
                        title={bead.label}
                      />
                    );
                  } else {
                    return (
                      <div 
                        key={idx}
                        className="w-7 h-7 bg-white border-2 border-gray-400 rounded-full font-black text-xs text-gray-700 shadow-md flex items-center justify-center transform hover:scale-115 transition-transform duration-200 cursor-pointer shrink-0 select-none"
                      >
                        {bead.letter}
                      </div>
                    );
                  }
                })}
                
                {currentBeadString.length === 0 && (
                  <span className="text-gray-400 text-xs italic bg-white px-3 py-1.5 rounded-full z-10 border border-gray-100 shadow-sm">
                    Choose colors or add letters above
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="max-w-xs text-xs text-gray-500 space-y-2">
            <p className="font-bold text-gray-700">How Custom Orders Work:</p>
            <p>1. Design your custom colors/names above.</p>
            <p>2. Georgia will handcraft it to your specifications.</p>
            <p>3. Standard custom orders are ready to ship within 3–5 business days.</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Customize;
